import {
  ALLOWED_ATTACHMENT_MIME_TYPES,
  EmailAttachmentUploadUrlResponse,
  EmailRequestType,
  GetEmailByIdRequest,
  isUnifiedAttachmentRef,
  MessagingPlatform,
  SearchEmailsRequest,
  UnifiedAttachmentRef,
  UnifiedEmailErrorCode,
  UnifiedSendAttachment,
  UnifiedSendEmailErrorResponse,
  UnifiedSendEmailRequest,
  UnifiedSendEmailResponse,
} from '@this+that/models';
import axios from 'axios';
import log from 'loglevel';

import { useUserCommunicationsStore } from '../store/Inbox/UserCommunications';
import { createApiWithAuth } from '../utils/axios';
import {
  EMAIL_ATTACHMENT_REUSE_WINDOW_MS,
  EMAIL_ATTACHMENT_UPLOAD_URL_PATH,
  HTTP_STATUS_PAYLOAD_TOO_LARGE,
  MAX_EMAIL_REQUEST_BYTES,
  S3_ATTACHMENT_PLATFORMS,
} from '../utils/constants/drafts';
import { EMPTY_CONTENT_PATTERNS } from '../utils/editorContentUtils';
import { fileAttachmentFromBase64 } from '../utils/fileAttachmentFromBase64';
import { uploadToPresignedPost } from '../utils/fileHandlers/presignedUpload';

const DRAFT_VERSION_CONFLICT_MESSAGE = 'Draft version changed on the server';
const PAYLOAD_TOO_LARGE_MESSAGE = 'Email request exceeds the payload limit';

export class DraftVersionConflictError extends Error {
  readonly currentDraftVersion?: string;

  constructor(message: string, currentDraftVersion?: string) {
    super(message);
    this.name = 'DraftVersionConflictError';
    this.currentDraftVersion = currentDraftVersion;
  }
}

export class RequestPayloadTooLargeError extends Error {
  constructor(message: string = PAYLOAD_TOO_LARGE_MESSAGE) {
    super(message);
    this.name = 'RequestPayloadTooLargeError';
  }
}

const isPayloadTooLargeResponse = (error: unknown): boolean =>
  axios.isAxiosError(error) &&
  error.response?.status === HTTP_STATUS_PAYLOAD_TOO_LARGE;

// Detects the conflict by HTTP status or errorCode only, never by matching the
// (potentially localized/redacted) message string.
const extractDraftVersionConflict = (
  error: unknown,
): UnifiedSendEmailErrorResponse | undefined => {
  if (!axios.isAxiosError(error)) {
    return undefined;
  }

  const body = error.response?.data?.message as
    | UnifiedSendEmailErrorResponse
    | undefined;

  const isConflict =
    error.response?.status === 409 ||
    body?.errorCode === UnifiedEmailErrorCode.DRAFT_VERSION_CONFLICT;

  return isConflict ? body : undefined;
};

const RETRYABLE_ATTACHMENT_ERRORS: ReadonlySet<UnifiedEmailErrorCode> = new Set(
  [
    UnifiedEmailErrorCode.ATTACHMENT_INVALID,
    UnifiedEmailErrorCode.ATTACHMENT_EXPIRED,
  ],
);

const handleRequestError = (context: string, error: unknown) => {
  if (error instanceof RequestPayloadTooLargeError) {
    throw error;
  }
  if (isPayloadTooLargeResponse(error)) {
    log.warn(`Unified Email Service payload too large [${context}]`);
    throw new RequestPayloadTooLargeError();
  }

  const conflict = extractDraftVersionConflict(error);
  if (conflict) {
    throw new DraftVersionConflictError(
      conflict.message ?? DRAFT_VERSION_CONFLICT_MESSAGE,
      conflict.currentDraftVersion,
    );
  }

  log.error(`Unified Email Service Error [${context}]:`, error);
  throw error;
};

let unifiedEmailServiceApi: ReturnType<typeof createApiWithAuth> | null = null;

const getUnifiedEmailServiceApi = () => {
  if (!unifiedEmailServiceApi) {
    const baseUrl = import.meta.env.VITE_UNIFIED_EMAIL_BASE_URL;
    if (!baseUrl) {
      throw new Error('Unified Email Service URL is not set');
    }
    unifiedEmailServiceApi = createApiWithAuth(baseUrl);
  }
  return unifiedEmailServiceApi;
};

interface UploadedAttachment {
  s3Key: Promise<string>;
  uploadedAt: number;
}

// Keyed by File identity: the same attachment across autosaves uploads once per tab.
const uploadedAttachments = new WeakMap<File, UploadedAttachment>();

// Files the server rejected as INVALID as S3 refs: they go inline from then on.
const inlineOnlyFiles = new WeakSet<File>();

const uploadAttachment = async (file: File): Promise<string> => {
  const { data } = await getUnifiedEmailServiceApi().post<{
    message: EmailAttachmentUploadUrlResponse;
  }>(EMAIL_ATTACHMENT_UPLOAD_URL_PATH, {
    fileType: file.type,
    fileSize: file.size,
  });
  const { url, fields, s3Key } = data.message;
  await uploadToPresignedPost({ url, fields }, file);
  return s3Key;
};

const getUploadedKey = (file: File): Promise<string> => {
  const cached = uploadedAttachments.get(file);
  if (
    cached &&
    Date.now() - cached.uploadedAt < EMAIL_ATTACHMENT_REUSE_WINDOW_MS
  ) {
    return cached.s3Key;
  }
  const s3Key = uploadAttachment(file);
  uploadedAttachments.set(file, { s3Key, uploadedAt: Date.now() });
  s3Key.catch(() => uploadedAttachments.delete(file));
  return s3Key;
};

export const forgetUploadedAttachments = (
  files: Array<File | undefined> = [],
) => {
  for (const file of files) {
    if (file) uploadedAttachments.delete(file);
  }
};

export type EmailRequestAttachment = UnifiedSendAttachment & { file?: File };

export type EmailSendRequest = Omit<UnifiedSendEmailRequest, 'attachments'> & {
  attachments?: EmailRequestAttachment[];
};

// Gmail and Outlook take S3 refs; Files without an allow-listed type (reopened drafts) and anything without a File stay inline.
export const toEmailApiAttachments = async (
  attachments: EmailRequestAttachment[] | undefined,
  platform: MessagingPlatform,
): Promise<UnifiedSendAttachment[] | undefined> => {
  if (!attachments?.length) return undefined;
  return Promise.all(
    attachments.map(async ({ file, ...attachment }) =>
      S3_ATTACHMENT_PLATFORMS.has(platform) &&
      file &&
      !inlineOnlyFiles.has(file) &&
      ALLOWED_ATTACHMENT_MIME_TYPES.has(file.type)
        ? {
            name: attachment.name,
            contentType: attachment.contentType,
            s3Key: await getUploadedKey(file),
          }
        : attachment,
    ),
  );
};

export class UnschedulableAttachmentError extends Error {
  readonly fileName: string;

  constructor(fileName: string) {
    super(`Attachment cannot be scheduled: ${fileName}`);
    this.name = 'UnschedulableAttachmentError';
    this.fileName = fileName;
  }
}

// The scheduling API accepts S3 refs only: reopened-draft bytes are uploaded here, an unscheduleable file throws.
export const toScheduledEmailAttachments = async (
  attachments: EmailRequestAttachment[] | undefined,
): Promise<UnifiedAttachmentRef[] | undefined> => {
  if (!attachments?.length) return undefined;
  return Promise.all(
    attachments.map(async ({ file, ...attachment }) => {
      if (isUnifiedAttachmentRef(attachment) && attachment.s3Key) {
        return {
          name: attachment.name,
          contentType: attachment.contentType,
          s3Key: attachment.s3Key,
        };
      }
      const source =
        file ??
        ('contentBytes' in attachment
          ? fileAttachmentFromBase64(attachment).file
          : undefined);
      if (!source) throw new UnschedulableAttachmentError(attachment.name);
      if (
        inlineOnlyFiles.has(source) ||
        !ALLOWED_ATTACHMENT_MIME_TYPES.has(source.type)
      ) {
        throw new UnschedulableAttachmentError(attachment.name);
      }
      return {
        name: attachment.name,
        contentType: attachment.contentType,
        s3Key: await getUploadedKey(source),
      };
    }),
  );
};

const sanitizeValue = <T>(value: T): T | undefined => {
  if (Array.isArray(value)) {
    return value.length > 0 ? value : undefined;
  }

  if (typeof value === 'string') {
    const trimmed = value.trim();
    if (
      trimmed === EMPTY_CONTENT_PATTERNS.EMPTY ||
      trimmed === EMPTY_CONTENT_PATTERNS.PARAGRAPH ||
      trimmed === EMPTY_CONTENT_PATTERNS.PARAGRAPH_BR ||
      trimmed === EMPTY_CONTENT_PATTERNS.DIV_BR ||
      trimmed === EMPTY_CONTENT_PATTERNS.BR
    ) {
      return undefined;
    }
    return trimmed as T;
  }

  if (value === null || value === undefined) {
    return undefined;
  }

  return value;
};

export const searchEmails = async ({
  from,
  to,
  subject,
  keywords,
  isRead,
  hasAttachment,
  labels,
  after,
  before,
  maxResults,
  pageTokens,
}: SearchEmailsRequest) => {
  try {
    const { data } = await getUnifiedEmailServiceApi().post('/emails/search', {
      from,
      to,
      subject,
      keywords,
      isRead,
      hasAttachment,
      labels,
      after,
      before,
      maxResults,
      pageTokens,
    });

    const {
      message: { messages, nextPageTokens, totalCount },
    } = data;

    return { messages, nextPageTokens, totalCount };
  } catch (error) {
    return handleRequestError('searching emails', error);
  }
};

export const getEmailById = async ({
  messageId,
  provider,
  accountId,
}: GetEmailByIdRequest) => {
  const { data } = await getUnifiedEmailServiceApi().get(`/emails/get-by-id`, {
    params: {
      messageId,
      provider,
      accountId,
    },
  });
  return data;
};

const buildSendRequestBody = ({
  providerAccount,
  reqType,
  subject,
  body,
  toAddresses,
  ccAddresses,
  bccAddresses,
  messageId,
  draftId,
  draftType,
  additionalText,
  attachments,
  attachmentsToRemove,
  modifiedQuotedContent,
  expectedDraftVersion,
}: UnifiedSendEmailRequest) => {
  const isClearingExistingDraftBody =
    reqType === EmailRequestType.DRAFT &&
    Boolean(draftId) &&
    (body === EMPTY_CONTENT_PATTERNS.EMPTY ||
      body === EMPTY_CONTENT_PATTERNS.PARAGRAPH_BR);

  return {
    providerAccount,
    reqType,
    subject: sanitizeValue(subject),
    body: isClearingExistingDraftBody ? body : sanitizeValue(body),
    // The compositor owns the full reviewed recipient list for DRAFT/SEND_DRAFT, so an
    // empty array must reach the provider as "clear this field" — never collapsed to
    // undefined ("keep existing"), or a removed Cc/Bcc/To recipient still gets the email.
    toAddresses,
    ccAddresses,
    bccAddresses,
    messageId: sanitizeValue(messageId),
    draftId,
    draftType,
    additionalText: sanitizeValue(additionalText),
    attachments: sanitizeValue(attachments),
    attachmentsToRemove: sanitizeValue(attachmentsToRemove),
    modifiedQuotedContent: sanitizeValue(modifiedQuotedContent),
    expectedDraftVersion,
  };
};

// Measures the exact JSON the request will carry, so the request never leaves for a guaranteed 413.
const assertWithinRequestLimit = (requestBody: object) => {
  const bytes = new Blob([JSON.stringify(requestBody)]).size;
  if (bytes > MAX_EMAIL_REQUEST_BYTES) {
    log.warn('Email request blocked before sending, over the payload limit', {
      bytes,
    });
    throw new RequestPayloadTooLargeError();
  }
};

const postEmailRequest = async (request: EmailSendRequest) => {
  const attachments = await toEmailApiAttachments(
    request.attachments,
    request.providerAccount.provider,
  );
  const requestBody = buildSendRequestBody({ ...request, attachments });
  assertWithinRequestLimit(requestBody);

  const { data } = await getUnifiedEmailServiceApi().post<{
    message: UnifiedSendEmailResponse;
  }>('/emails/send', requestBody);

  return data;
};

const syncDraftResponseToStore = (result: UnifiedSendEmailResponse) => {
  const { getUserCommunicationByDraftId, upsertDraft } =
    useUserCommunicationsStore.getState();

  // Skipped when the row does not exist yet: the auto-save commit that follows writes the full row.
  if (result.draftId && getUserCommunicationByDraftId(result.draftId)) {
    upsertDraft({
      draftId: result.draftId,
      messageId: result.messageId,
      messageRawText: result.messageRawText,
    });
  }
};

const postSendEmails = async (request: EmailSendRequest) => {
  const data = await postEmailRequest(request);

  if (request.reqType === EmailRequestType.DRAFT && data.message) {
    syncDraftResponseToStore(data.message);
  }

  return data;
};

export const sendEmailsWithoutStoreSync = async (request: EmailSendRequest) => {
  try {
    return await postEmailRequest(request);
  } catch (error) {
    return handleRequestError('probing draft', error);
  }
};

const extractAttachmentError = (
  error: unknown,
): UnifiedSendEmailErrorResponse | undefined => {
  if (!axios.isAxiosError(error)) {
    return undefined;
  }
  const body = error.response?.data?.message as
    | UnifiedSendEmailErrorResponse
    | undefined;
  return body && RETRYABLE_ATTACHMENT_ERRORS.has(body.errorCode)
    ? body
    : undefined;
};

// The server validates attachments before any provider call, so a retry can't double-send.
// Returns true when the rejected Files were fixed and the request is worth resending once.
const prepareAttachmentRetry = (
  error: unknown,
  request: EmailSendRequest,
): boolean => {
  const body = extractAttachmentError(error);
  if (!body) return false;

  const rejected = (request.attachments ?? []).flatMap(({ file, name }) =>
    file && name === body.fileName ? [file] : [],
  );
  for (const file of rejected) {
    uploadedAttachments.delete(file);
    if (body.errorCode === UnifiedEmailErrorCode.ATTACHMENT_INVALID) {
      inlineOnlyFiles.add(file);
    }
  }
  return rejected.length > 0;
};

export const sendEmails = async (request: EmailSendRequest) => {
  try {
    return await postSendEmails(request);
  } catch (error) {
    if (!prepareAttachmentRetry(error, request)) {
      return handleRequestError('sending email', error);
    }
  }

  try {
    return await postSendEmails(request);
  } catch (error) {
    return handleRequestError('sending email', error);
  }
};
